// Glissement à deux doigts au trackpad — fonctions pures, sans DOM. (Au doigt, la souris est
// traînée librement : voir maze.slideToward.)
// Le trajet du geste est cumulé depuis le dernier pas. Dès qu'il atteint `stepPx` sur un axe, il
// devient une intention de pas : la direction dominante, et la direction perpendiculaire si le
// geste penche assez de ce côté (secours quand un mur bloque, voir maze.resolveSwipeStep).
import { SWIPE_SECONDARY_RATIO, SWIPE_MIN_PX } from '../config/constants.js';

/**
 * Distance de glissement pour un pas : environ une case (le doigt suit la souris quand les cases
 * sont petites), bornée entre SWIPE_MIN_PX (le tremblement du doigt ne compte pas) et `maxPx`
 * (réglage dev : pas besoin de glisser loin quand les cases sont grandes).
 */
export function swipeStepPx(cellPx, maxPx) {
  return Math.max(SWIPE_MIN_PX, Math.min(maxPx, cellPx));
}

/**
 * @param {{x:number, y:number}} acc trajet cumulé depuis le dernier pas (px)
 * @returns {{primary:string, secondary:string|null}|null} null tant que le seuil n'est pas atteint
 */
export function swipeIntent(acc, stepPx) {
  const ax = Math.abs(acc.x);
  const ay = Math.abs(acc.y);
  if (Math.max(ax, ay) < stepPx) return null;
  const horizontal = acc.x > 0 ? 'right' : 'left';
  const vertical = acc.y > 0 ? 'down' : 'up';
  const [primary, secondary, minor] = ax >= ay ? [horizontal, vertical, ay] : [vertical, horizontal, ax];
  return { primary, secondary: minor >= stepPx * SWIPE_SECONDARY_RATIO ? secondary : null };
}

const isHorizontal = (dir) => dir === 'left' || dir === 'right';
const signOf = (dir) => (dir === 'right' || dir === 'down' ? 1 : -1);

/** Retire `amount` px du trajet sur l'axe de `dir`, sans dépasser zéro. */
function consume(acc, dir, amount) {
  const key = isHorizontal(dir) ? 'x' : 'y';
  const value = acc[key] * signOf(dir); // part du trajet dans le sens de dir
  return { ...acc, [key]: signOf(dir) * Math.max(0, value - amount) };
}

/**
 * Trajet restant après un pas réussi dans `moved`. Le trajet consommé est celui du doigt dans ce
 * sens s'il y en a ; sinon (virage pris en suivant le couloir) celui de la direction dominante.
 * L'autre axe est ensuite amorti : une légère dérive perpendiculaire ne doit pas finir par
 * déclencher un pas de travers.
 */
export function afterSwipeStep(acc, intent, moved, stepPx) {
  const key = isHorizontal(moved) ? 'x' : 'y';
  const fingerWentThatWay = acc[key] * signOf(moved) >= stepPx * SWIPE_SECONDARY_RATIO;
  const used = fingerWentThatWay ? moved : intent.primary;
  const next = consume(acc, used, stepPx);
  const other = isHorizontal(used) ? 'y' : 'x';
  const limit = stepPx * SWIPE_SECONDARY_RATIO;
  return { ...next, [other]: Math.max(-limit, Math.min(limit, next[other])) };
}

/**
 * Trajet après un pas bloqué : le trajet dans la direction bloquée est plafonné au seuil, pour
 * qu'il ne s'accumule pas contre le mur. La souris reste « en appui » : le moindre penchant
 * perpendiculaire du geste (voir SWIPE_SECONDARY_RATIO) la fait tourner aussitôt.
 */
export function afterSwipeBlocked(acc, intent, stepPx) {
  const key = isHorizontal(intent.primary) ? 'x' : 'y';
  return { ...acc, [key]: Math.sign(acc[key]) * Math.min(Math.abs(acc[key]), stepPx) };
}

/**
 * Joue tous les pas que contient le trajet (un geste rapide peut en franchir plusieurs entre deux
 * événements), jusqu'au reliquat sous le seuil ou jusqu'au premier pas bloqué.
 * @param {(intent) => string|null} tryStep tente le pas, renvoie la direction réellement prise
 * @returns {{x:number, y:number}} le trajet restant
 */
export function playSwipe(acc, stepPx, tryStep, maxSteps = 12) {
  let current = acc;
  for (let i = 0; i < maxSteps; i++) {
    const intent = swipeIntent(current, stepPx);
    if (!intent) break;
    const moved = tryStep(intent);
    if (!moved) return afterSwipeBlocked(current, intent, stepPx);
    current = afterSwipeStep(current, intent, moved, stepPx);
  }
  return current;
}
